import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SETUP_ITEMS = [
  'Pipeline stages configured',
  'Job types loaded',
  'Document templates created',
  'Call scripts ready',
  'Marketing templates added',
  'Custom fields applied',
  'Dashboard configured',
];

const SetupCompleteStep = ({ businessType, onComplete, isSeeding }) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [allDone, setAllDone] = useState(false);

  const businessLabel = businessType
    ? businessType?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase())
    : 'Your Business';

  useEffect(() => {
    setVisibleItems([]);
    setAllDone(false);
    let i = 0;
    const interval = setInterval(() => {
      if (i < SETUP_ITEMS?.length) {
        setVisibleItems((prev) => [...prev, i]);
        i++;
      } else {
        clearInterval(interval);
        setTimeout(() => setAllDone(true), 400);
      }
    }, 320);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-16 h-16 bg-gradient-to-br from-[#22C55E] to-[#16A34A] rounded-2xl flex items-center justify-center mx-auto mb-4"
        >
          <span className="text-3xl">🚀</span>
        </motion.div>
        <h2 className="text-2xl font-bold text-foreground">Your CRM is being set up!</h2>
        <p className="text-muted-foreground mt-2">
          Personalizing everything for <span className="font-semibold text-primary">{businessLabel}</span>
        </p>
      </div>
      <div className="max-w-sm mx-auto space-y-2">
        {SETUP_ITEMS?.map((item, idx) => (
          <AnimatePresence key={idx}>
            {visibleItems?.includes(idx) && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-green-800 font-medium">{item}</span>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
      <AnimatePresence>
        {allDone && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center pt-2"
          >
            <button
              onClick={onComplete}
              disabled={isSeeding}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSeeding ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Setting up...</span>
                </span>
              ) : 'Go to my CRM 🎉'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SetupCompleteStep;
